import { useEffect, useState, useCallback } from 'react'
import { useModal } from 'store/selectors'
import useModalsActions from 'store/actions/modals'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Loader from 'components/core/Loader'
import api from 'services/api'

const Vignette = ({ vignette, index }) => {
  const startTime = vignette.start_time_in_seconds
  const endTime = vignette.end_time_in_seconds
  const hasTime = startTime != null && endTime != null

  return (
    <Box sx={{ mb: 2, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
      >
        #{index + 1}
        {hasTime && ` · ${startTime}s – ${endTime}s`}
        {vignette.characters_in_scene?.length > 0 &&
          ` · ${vignette.characters_in_scene.join(', ')}`}
        {vignette.locations_in_scene?.length > 0 &&
          ` · ${vignette.locations_in_scene.join(', ')}`}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {vignette.directors_notes}
      </Typography>
    </Box>
  )
}

const EmoticonScene = ({ scene, index }) => (
  <Box sx={{ mb: 2, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
    >
      #{index + 1}
      {scene.start_timestamp != null &&
        scene.end_timestamp != null &&
        ` · ${scene.start_timestamp}s – ${scene.end_timestamp}s`}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography variant="h5" component="span">
        {scene.primary_emoji}
      </Typography>
      <Typography variant="subtitle2">{scene.primary_label}</Typography>
    </Box>
    {scene.secondary_emojis?.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {scene.secondary_emojis.map((se, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              backgroundColor: 'action.hover',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
            }}
          >
            {se.emoji} {se.label}{' '}
            {se.timestamp != null && (
              <span style={{ opacity: 0.6 }}>@{se.timestamp}s</span>
            )}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
)

const Section = ({ title, count, children, defaultExpanded = false }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{
      backgroundColor: 'transparent',
      boxShadow: 'none',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ px: 0, minHeight: 40 }}
    >
      <Typography variant="subtitle2">
        {title}{' '}
        {count != null && <span style={{ opacity: 0.5 }}>({count})</span>}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
)

const AnimationOutline = () => {
  const { isOpen, params } = useModal('animationOutline')
  const sourceId = params?.sourceId
  const { closeModal } = useModalsActions()
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!isOpen || !sourceId) return
    let active = true
    setData(null)
    api.animatedVideos
      .getAnimatedVideoDetails(sourceId)
      .then((res) => {
        if (active) setData(res || {})
      })
      .catch(() => {
        if (active) setData({ _error: true })
      })
    return () => {
      active = false
    }
  }, [isOpen, sourceId])

  const onClose = useCallback(() => {
    closeModal('animationOutline')
  }, [closeModal])

  const onExited = useCallback(() => {
    setData(null)
  }, [])

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxHeight: '70vh',
          width: 'min(1200px, 85vw)',
          padding: '20px',
        },
      }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Animation Outline
      </Typography>
      {!data ? (
        <Loader text="loading animation details" />
      ) : data._error ? (
        <Typography color="error">Failed to load outline.</Typography>
      ) : !(
          data.creative_strategy ||
          data.style ||
          data.vignettes?.length ||
          data.scenes?.length ||
          data.characters?.length ||
          data.locations?.length ||
          data.title_context ||
          data.animation_context
        ) ? (
        <Typography
          sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
        >
          No outline details available.
        </Typography>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          {data.creative_strategy && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Creative Strategy
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
              >
                {data.creative_strategy}
              </Typography>
            </Box>
          )}

          {data.style && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Style
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
              >
                {data.style}
              </Typography>
            </Box>
          )}

          {data.vignettes?.length > 0 && (
            <Section title="Vignettes" count={data.vignettes.length}>
              {data.vignettes.map((v, i) => (
                <Vignette key={i} vignette={v} index={i} />
              ))}
            </Section>
          )}

          {data.scenes?.length > 0 && (
            <Section title="Scenes" count={data.scenes.length} defaultExpanded>
              {data.scenes.map((s, i) => (
                <EmoticonScene key={i} scene={s} index={i} />
              ))}
            </Section>
          )}

          {data.characters?.length > 0 && (
            <Section title="Characters" count={data.characters.length}>
              {data.characters.map((c, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">{c.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {c.description}
                  </Typography>
                </Box>
              ))}
            </Section>
          )}

          {data.locations?.length > 0 && (
            <Section title="Locations" count={data.locations.length}>
              {data.locations.map((l, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">{l.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {l.description}
                  </Typography>
                </Box>
              ))}
            </Section>
          )}

          {data.title_context && (
            <Section title="Title Context">
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2">
                  <b>
                    {data.title_context.title_source === 'user'
                      ? 'User-provided'
                      : 'Auto-generated'}
                    :
                  </b>{' '}
                  "{data.title_context.title}"
                </Typography>
                {data.title_context.alt_titles?.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                  >
                    Alt titles: {data.title_context.alt_titles.join(', ')}
                  </Typography>
                )}
              </Box>
            </Section>
          )}

          {data.animation_context && (
            <Section title="Animation Context">
              <Typography
                variant="body2"
                sx={{ pl: 1, whiteSpace: 'pre-wrap' }}
              >
                {data.animation_context}
              </Typography>
            </Section>
          )}

          {(data.latency_ms != null || data.prompt_tokens != null) && (
            <Box
              sx={{
                mt: 2,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {data.latency_ms != null &&
                  `Latency: ${(data.latency_ms / 1000).toFixed(1)}s`}
                {data.latency_ms != null && data.prompt_tokens != null && ' · '}
                {data.prompt_tokens != null &&
                  `Tokens: ${data.prompt_tokens} in / ${
                    data.completion_tokens ?? '?'
                  } out`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  )
}

export default AnimationOutline
